import React, { useState, useMemo } from "react";
import axiosInstance from "../api/axiosInstance";
import { showGlobalToast } from '../utils/toast';

import { motion, AnimatePresence } from "framer-motion";
import { Mail, MessageSquare, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";

interface SendInvoiceModalProps {
    name: string;
    identifier: string;
    isMobile: boolean;
    paymentId: string;
    paramKey?: string;
    paramValue?: string;
    invoiceNo?: string;
    onClose: () => void;
}

type Mode = "WHATSAPP" | "SMS" | "EMAIL";

const modeOptions: { mode: Mode; label: string; icon: React.ElementType; color: string }[] = [
    { mode: "WHATSAPP", label: "WhatsApp", icon: FaWhatsapp, color: 'text-green-500' },
    { mode: "SMS", label: "SMS", icon: MessageSquare, color: 'text-indigo-500' },
    { mode: "EMAIL", label: "Email", icon: Mail, color: 'text-red-500' },
];

const SendInvoiceModal: React.FC<SendInvoiceModalProps> = ({
    name,
    identifier,
    isMobile,
    paymentId,
    paramKey,
    paramValue,
    invoiceNo,
    onClose,
}) => {
    const defaultMode = isMobile ? 'SMS' : 'EMAIL';
    const [selectedMode, setSelectedMode] = useState<Mode | null>(defaultMode);
    const [contact, setContact] = useState(identifier || '');
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState<string | null>(null);
    const isCurrentModeMobile = useMemo(() => {
        return selectedMode === 'EMAIL' ? false : true;
    }, [selectedMode]);

    const currentIdentifierLabel = isCurrentModeMobile ? 'Mobile Number' : 'Email Address';
    const isValidContact = useMemo(() => {
        if (!contact.trim()) return false;

        const isMobileValid = /^[0-9]{10}$/.test(contact);
        const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact);

        return isCurrentModeMobile ? isMobileValid : isEmailValid;
    }, [isCurrentModeMobile, contact]);

    const isConfirmEnabled = useMemo(() => {
        return selectedMode !== null && isValidContact;
    }, [selectedMode, isValidContact]);


    const handleConfirm = async () => {
        if (!isConfirmEnabled) return;

        setLoading(true);
        setApiError(null);

        try {
            const res = await axiosInstance.post(`/Billing/SendInvoice`, {
                paymentId: paymentId ,
                mode: selectedMode,
                identifier: contact,
                name: name,
                paramKey: paramKey,
                paramValue: paramValue,
                invoiceNo: invoiceNo,
            });

            if (res.data.isSent) {
                const successMessage = `Invoice sent successfully via ${selectedModeObj?.label}!`;
                showGlobalToast(successMessage, "success");
                setTimeout(onClose, 1500);
            }
            else {
                setApiError(res.data.message || 'Failed to send invoice');
            }
        } catch (err) {
            const errorMessage = `Failed to send invoice via ${selectedModeObj?.label}.`;
            setApiError(errorMessage);
            showGlobalToast(errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const selectedModeObj = modeOptions.find(m => m.mode === selectedMode);
    const SelectedIcon = selectedModeObj?.icon || null;

    return (
        <AnimatePresence>
            <motion.div
                className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70 backdrop-blur-sm p-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
            >
                <motion.div
                    className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-6 sm:p-8 transform transition-all duration-300 scale-100 relative"
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                >

                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
                        aria-label="Close modal"
                        disabled={loading}
                    >
                        <XCircle size={24} />
                    </button>

                    <h3 className="text-2xl font-extrabold text-gray-900 dark:text-white mb-2 border-b border-gray-200 dark:border-gray-700 pb-2">
                        Send Invoice
                    </h3>

                    {/* 1. Mode Selection (Segmented Control) */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Select Mode:
                        </label>
                        <div className="flex justify-between space-x-2 p-1 bg-gray-100 dark:bg-gray-700 rounded-xl shadow-inner">
                            {modeOptions.map((m) => {
                                const ModeIcon = m.icon;
                                return (
                                    <button
                                        key={m.mode}
                                        onClick={() => setSelectedMode(m.mode)}
                                        disabled={loading}
                                        className={`
                      flex items-center justify-center flex-1 py-2 px-1 rounded-xl transition duration-200 font-semibold text-sm
                      ${selectedMode === m.mode
                                                ? `bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-md ring-2 ring-orange-400`
                                                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'}
                      ${loading ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                                    >
                                        <ModeIcon size={16} className={`mr-1 ${m.color}`} />
                                        {m.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* 2. Recipient Information */}
                    <div className="space-y-4 mb-8 p-4 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-700/50">

                        {/* Name Field (Read-Only) */}
                        <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Customer Name:</span>
                            <span className="text-base font-semibold text-gray-900 dark:text-white">{name || 'N/A'}</span>
                        </div>

                        {/* Identifier Field (Editable) */}
                        <div>
                            <label className="flex items-center text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
                                {currentIdentifierLabel}:
                                {/* Show validation status if contact is present */}
                                {contact.length > 0 && (
                                    <span className={`ml-2 text-xs font-normal ${isValidContact ? 'text-green-500' : 'text-red-500'}`}>
                                        {isValidContact ? '(Valid)' : '(Invalid)'}
                                    </span>
                                )}
                            </label>
                            <input
                                // Dynamic input type based on the current mode
                                type={isCurrentModeMobile ? 'tel' : 'email'}
                                value={contact}
                                onChange={(e) => setContact(e.target.value)}
                                disabled={loading}
                                className={`
                                    w-full p-2 border rounded-lg text-gray-900 dark:text-white dark:bg-gray-900 focus:ring-orange-400 focus:border-orange-500 transition
                                    ${loading ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : 'bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600'}
                                    ${!isValidContact && contact.length > 0 ? 'border-red-500 ring-red-500' : ''}
                                `}
                                placeholder={`Enter ${currentIdentifierLabel} (Required)`}
                            />
                        </div>
                    </div>

                    {/* 3. Action Buttons & Status */}
                    <div className="flex justify-end space-x-3">

                        {/* Status Icon/Text */}
                        {SelectedIcon && isConfirmEnabled && (
                            <div className="flex items-center text-sm font-medium text-orange-500 dark:text-orange-400 mr-auto">
                                <SelectedIcon size={21} className={`mr-2 ${selectedModeObj?.color || ''}`} />
                                Ready to send via {selectedModeObj?.label ?? ''}
                            </div>
                        )}

                        {/* Show error/warning message if not enabled */}
                        {(!isConfirmEnabled && !loading) && (
                            <div className="flex items-center text-sm font-medium text-red-600 dark:text-red-400 mr-auto">
                                <XCircle size={21} className="mr-2" />
                                Select mode & ensure valid contact
                            </div>
                        )}

                        <button
                            onClick={onClose}
                            disabled={loading}
                            className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                        >
                            Cancel
                        </button>

                        <button
                            onClick={handleConfirm}
                            disabled={!isConfirmEnabled || loading}
                            className={`
                                flex items-center px-6 py-2 text-sm font-bold rounded-xl transition duration-300 shadow-lg 
                                ${isConfirmEnabled && !loading ? 'bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 text-white transform hover:scale-[1.02]'
                                    : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'}
                            `}
                        >
                            {loading ? (
                                // Loading Spinner
                                <>
                                    <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <CheckCircle size={18} className="mr-2" />
                                    Confirm
                                </>
                            )}
                        </button>
                    </div>

                    {/* API Feedback (Error) */}
                    {apiError && (
                        <div className="mt-4 p-3 rounded-lg flex items-center bg-red-100 text-red-700 dark:bg-red-800/50 dark:text-red-300">
                            <XCircle size={18} className="mr-2" />
                            <span className="text-sm">{apiError}</span>
                        </div>
                    )}
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default SendInvoiceModal;
